// The `url` widget: a text box for a URL, with a button that fills it in from
// the site's own files.
//
// Ported from the archived project's @repoflow/components/widgets/url, which
// this repo carries an unbuildable copy of — it imports `components/
// file-selector` and `components/file`, neither of which was ported, so naming
// it from a schema threw `widget.missing:url` from inside every render of that
// type. That is why `faviconUrl` was a plain text box and says so in
// common/schema/page-config/fields. It is not one any more.
//
// It is here rather than in @repoflow/components for the reason ../paragraph
// gives at length: the picker behind it is this service's file listing, its
// schema and its URL builder, and a widget in a shared package cannot reach
// any of them.
//
// WHAT IT IS AND IS NOT
//
// A string field. The value is the URL and nothing else, which is what
// separates it from ../image — that one is an object with a description beside
// the URL, because an image on a page needs alt text and a favicon does not.
//
// It stays a TEXT BOX with a button, not a button alone. Every field this is on
// today accepts a URL that was never uploaded here — a favicon on a CDN, an
// image someone else hosts — and taking that away to add a picker would be a
// removal dressed as a feature.
import React, { useState } from "react";
import { Input } from "reactstrap";
import * as FileSelector from "components/file-selector";
import { IMAGES } from "@common/schema/site-file/kind";

export const View = ({ value }) => <span>{value}</span>;

export const Edit = (props) => {
  const {
    value,
    setValue,
    field: { name, property = {}, config = {} } = {},
    issues = [],
    labelId,
    describedById,
  } = props;

  const [touched, setTouched] = useState(false);
  const readOnly = Boolean(config.readOnly || property.readOnly);

  // Which tab of the Files section the picker opens on. Declared on the schema
  // property rather than passed in, because the field is the only thing that
  // knows what it is for: a favicon comes from Images. `config` wins over the
  // property so a screen can override one field without a second schema.
  const kind = config.fileKind || property.fileKind || IMAGES;

  return (
    <>
      <div className="input-group">
        <Input
          name={name}
          type="text"
          // What names this control: the form's label is a <span id> in the
          // <dt> beside this <dd> and its description a <FormText id> under it,
          // and the object widget hands both ids in — the same two attributes
          // every built-in widget sets. Undefined in a table cell, and React
          // drops an undefined attribute.
          aria-labelledby={labelId}
          aria-describedby={describedById}
          readOnly={readOnly}
          value={value || ""}
          invalid={touched && issues.length !== 0}
          onChange={(e) => {
            setValue(e.target.value);
            setTouched(true);
          }}
        />
        {!readOnly && (
          <div className="input-group-append">
            <FileSelector.SelectButton
              kind={kind}
              onSelect={(url) => {
                setValue(url);
                setTouched(true);
              }}
            />
          </div>
        )}
      </div>

      {/* Under the control rather than beside it: this widget is used on
          narrow, ordinary rows, and a preview in the other column would push
          the label off. Renders nothing at all when there is no URL or the URL
          does not resolve. */}
      <FileSelector.Preview url={value} className="img-fluid border rounded mt-2" />
    </>
  );
};
